import React from 'react';
import {
  Bot,
  CheckCircle2,
  Cpu,
  ExternalLink,
  Globe,
  Plug,
  Search,
  ShieldCheck,
} from 'lucide-react';
import ExpandableImage from '../components/ExpandableImage';
import ProductPage, { ProductSection } from '../components/ProductPage';
import { useTranslations } from '../context/TranslationContext';
import usePageMetadata from '../hooks/usePageMetadata';

const PLUGIN_URL = 'https://chatgpt.com/plugins/plugin_asdk_app_6a6a218b6d8c81918cbc5424c5d77f7f';
const MCP_DOCS = 'https://legisratio.gitbook.io/docs/legisration-mcp-server/legisratio-mcp-server';
const WEB_PLATFORM = 'https://chat.legisratio.com/';

const COPY = {
  it: {
    eyebrow: 'MCP · Chat legale',
    title: 'L’intelligenza legale, dove ti serve.',
    description: 'Risposte legali affidabili, direttamente nel tuo flusso di lavoro AI.',
    openPlugin: 'Apri in ChatGPT',
    contact: 'Contattaci',
    subject: 'Richiesta informazioni LegisChat',

    channelsTitle: 'Flessibile e integrato nei tuoi flussi di lavoro',
    channelsIntro: 'Scegli la modalità di accesso ideale per le tue esigenze.',

    usesTitle: 'Ricerca legale ad alte prestazioni',
    usesIntro: 'Ricerca semantica, ragionamento su grafo e piena tracciabilità.',
    casesTitle: 'Casi d’uso pratici',

    compareTitle: 'Perché non è un chatbot generico',
    compareIntro: 'Progettato per la complessità normativa.',
    comparisonScrollHint: 'Scorri orizzontalmente per confrontare gli approcci →',

  },
  en: {
    eyebrow: 'MCP · Legal chat',
    title: 'Legal intelligence, wherever you work.',
    description: 'Trusted legal answers, right inside your AI workflow.',
    openPlugin: 'Open in ChatGPT',
    contact: 'Contact us',
    subject: 'LegisChat information request',

    channelsTitle: 'Flexible and integrated into your workflow',
    channelsIntro: 'Choose the access mode that best fits your requirements.',

    usesTitle: 'High-performance legal research',
    usesIntro: 'Semantic search, graph reasoning, and full traceability.',
    casesTitle: 'Practical use cases',

    compareTitle: 'Why it is not a generic chatbot',
    compareIntro: 'Purpose-built for legal complexity.',
    comparisonScrollHint: 'Scroll horizontally to compare approaches →',

  }
};

const SolutionsPage: React.FC = () => {
  const { currentLanguage } = useTranslations();
  const c = COPY[currentLanguage];
  usePageMetadata('LegisChat | LegisRatio', c.description);

  const channels = currentLanguage === 'it' ? [
    {
      title: 'Server MCP Universale',
      desc: 'Integra LegisChat in Claude, Cursor, agenti AI personalizzati e qualsiasi client compatibile con Model Context Protocol.',
      icon: Cpu,
      href: MCP_DOCS,
      action: 'Documentazione MCP',
      accent: 'from-cosmic-teal/20 to-transparent',
    },
    {
      title: 'Plugin Ufficiale ChatGPT',
      desc: 'Attiva il plugin direttamente nel marketplace di ChatGPT per interrogare la conoscenza normativa senza configurazioni.',
      icon: Plug,
      href: PLUGIN_URL,
      action: 'Installa Plugin',
      accent: 'from-mystic-violet/20 to-transparent',
    },
    {
      title: 'Web App Dedicata',
      desc: 'Un’interfaccia web dedicata per esplorare ricerca semantica, grafi normativi e citazioni ufficiali senza dipendenze esterne.',
      icon: Globe,
      href: WEB_PLATFORM,
      action: 'Accedi alla Web App',
      accent: 'from-cosmic-teal/20 to-transparent',
    },
  ] : [
    {
      title: 'Universal MCP Server',
      desc: 'Integrate LegisChat into Claude, Cursor, Custom AI Agents and any Model Context Protocol compatible client.',
      icon: Cpu,
      href: MCP_DOCS,
      action: 'MCP Documentation',
      accent: 'from-cosmic-teal/20 to-transparent',
    },
    {
      title: 'Official ChatGPT Plugin',
      desc: 'Activate the plugin directly from the ChatGPT marketplace to query legal knowledge with zero setup.',
      icon: Plug,
      href: PLUGIN_URL,
      action: 'Install Plugin',
      accent: 'from-mystic-violet/20 to-transparent',
    },
    {
      title: 'Dedicated Web App',
      desc: 'A dedicated web interface to explore semantic search, legal knowledge graphs, and official citations with zero external dependencies.',
      icon: Globe,
      href: WEB_PLATFORM,
      action: 'Access Web App',
      accent: 'from-cosmic-teal/20 to-transparent',
    },
  ];

  const capabilities = currentLanguage === 'it' ? [
    ['Ricerca Semantica Giuridica', 'Comprende la ratio della domanda oltre la semplice sovrapposizione di parole chiave.', Search],
    ['Ragionamento su Grafo', 'Analizza gerarchie delle fonti, modifiche, abrogazioni e sentenze collegate.', Bot],
    ['Tracciabilità Totale', 'Ogni affermazione riporta il link e il riferimento puntuale alla fonte ufficiale.', ShieldCheck],
  ] : [
    ['Legal Semantic Search', 'Understands the core legal query beyond simple keyword matches.', Search],
    ['Graph Reasoning', 'Analyzes source hierarchies, amendments, repeals, and connected case law.', Bot],
    ['Full Traceability', 'Every claim includes direct links and precise references to official sources.', ShieldCheck],
  ];

  const comparisonPoints = currentLanguage === 'it' ? [
    {
      title: 'Affidabilità risposte',
      generic: 'Possibili Allucinazioni',
      legischat: 'Solo Fonti certificate',
    },
    {
      title: 'Verificabilità',
      generic: 'Risposta non verificabile',
      legischat: 'Tracciabilità completa',
    },
    {
      title: 'Aggiornamenti',
      generic: 'Dati statici',
      legischat: 'Aggiornamento Continuo',
    },
    {
      title: 'Tipo di ricerca',
      generic: 'Ricerca Testuale',
      legischat: 'Ricerca Semantica',
    },
  ] : [
    {
      title: 'Answer reliability',
      generic: 'Potential hallucinations',
      legischat: 'Certified sources only',
    },
    {
      title: 'Verifiability',
      generic: 'Unverifiable answers',
      legischat: 'Full traceability',
    },
    {
      title: 'Updates',
      generic: 'Static data',
      legischat: 'Continuous updates',
    },
    {
      title: 'Search type',
      generic: 'Text search',
      legischat: 'Semantic search',
    },
  ];

  const cases = currentLanguage === 'it' ? [
    ['Ricerca normativa', 'Individua norme applicabili e collegamenti rilevanti in pochi secondi.', '/images/legischat/usecase-1.png'],
    ['Analisi trasversale', 'Confronta fonti e relazioni che attraversano più documenti.', '/images/legischat/usecase-2.png'],
    ['Verifica giurisprudenziale', 'Esplora precedenti, citazioni e contesto di una decisione.', '/images/legischat/usecase-3.png'],
  ] : [
    ['Regulatory research', 'Find applicable rules and relevant connections in seconds.', '/images/legischat/usecase-1.png'],
    ['Cross-document analysis', 'Compare sources and relationships spanning multiple documents.', '/images/legischat/usecase-2.png'],
    ['Case-law verification', 'Explore precedents, citations and the context of a decision.', '/images/legischat/usecase-3.png'],
  ];

  return (
    <ProductPage
      eyebrow={c.eyebrow}
      title={c.title}
      description={c.description}
      primaryLabel={c.openPlugin}
      primaryHref={PLUGIN_URL}
      contactLabel={c.contact}
      contactSubject={c.subject}
    >
      {/* Channels Overview Section */}
      <ProductSection title={c.channelsTitle} intro={c.channelsIntro}>
        <div className="grid gap-6 md:grid-cols-3">
          {channels.map((chan) => {
            const Icon = chan.icon;
            return (
              <div
                key={chan.title}
                className="glass-panel group relative flex flex-col justify-between rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
              >
                <div>
                  <Icon size={28} className="mb-6 text-cosmic-teal transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                  <h3 className="text-xl font-bold text-white group-hover:text-cosmic-teal transition-colors">
                    {chan.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-gray-400">
                    {chan.desc}
                  </p>
                </div>
                <div className="mt-8">
                  <a
                    href={chan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex w-full items-center justify-between rounded-xl border border-white/10 bg-gradient-to-r px-4 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-white/20 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cosmic-teal focus:ring-offset-2 focus:ring-offset-void-blue ${chan.accent}`}
                  >
                    {chan.action}
                    <ExternalLink size={16} aria-hidden="true" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </ProductSection>

      {/* Why Not Generic Chatbot Comparison Section */}
      <ProductSection title={c.compareTitle} intro={c.compareIntro} className="bg-white/[0.015]">
        <p className="mb-4 text-sm text-gray-400 md:hidden">{c.comparisonScrollHint}</p>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-deep-space/50 shadow-2xl backdrop-blur-sm">
          <div className="overflow-x-auto" tabIndex={0} aria-label={c.compareTitle}>
            <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th scope="col" className="sticky left-0 z-20 w-[34%] bg-deep-space/90 backdrop-blur-md px-6 py-6 border-r border-white/10">
                    <span className="sr-only">{currentLanguage === 'it' ? 'Caratteristica' : 'Capability'}</span>
                  </th>
                  <th scope="col" className="w-[33%] border-l border-white/10 px-6 py-6 text-lg font-bold text-gray-200">
                    {currentLanguage === 'it' ? 'AI Generici' : 'Generic AI'}
                  </th>
                  <th scope="col" className="w-[33%] border-l border-white/10 bg-indigo-500/[0.14] px-6 py-6 text-lg font-bold text-indigo-300">
                    LegisChat
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {comparisonPoints.map((item) => (
                  <tr key={item.title} className="transition-colors hover:bg-white/[0.02]">
                    <th scope="row" className="sticky left-0 z-20 bg-white/[0.035] backdrop-blur-md px-6 py-6 text-base font-semibold text-gray-200 border-r border-white/10">
                      {item.title}
                    </th>
                    <td className="border-l border-white/10 px-6 py-6 leading-relaxed text-gray-300">
                      <span className="flex items-start gap-2.5">
                        <span>{item.generic}</span>
                      </span>
                    </td>
                    <td className="border-l border-white/10 bg-indigo-500/[0.1] px-6 py-6 leading-relaxed text-white font-medium">
                      <span className="flex items-start gap-2.5">
                        <CheckCircle2 aria-hidden="true" className="mt-0.5 shrink-0 text-indigo-300" size={19} />
                        <span className="font-semibold text-white">{item.legischat}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ProductSection>

      {/* Core Capabilities Section */}
      <ProductSection title={c.usesTitle} intro={c.usesIntro}>
        <div className="grid gap-6 md:grid-cols-3">
          {capabilities.map(([title, text, Icon]) => (
            <div key={title as string} className="glass-panel rounded-2xl p-7 hover:border-white/20 transition-colors">
              <Icon className="mb-5 text-cosmic-teal" size={32} />
              <h3 className="text-xl font-bold">{title as string}</h3>
              <p className="mt-3 text-base leading-relaxed text-gray-400">{text as string}</p>
            </div>
          ))}
        </div>
      </ProductSection>

      {/* Use Cases Section */}
      <ProductSection title={c.casesTitle}>
        <div className="space-y-10">
          {cases.map(([title, text, image], index) => (
            <article key={title} className="grid gap-7 lg:grid-cols-2 lg:items-center">
              <div className={index % 2 ? 'lg:order-2' : ''}>
                <ExpandableImage
                  src={image}
                  alt={title}
                  containerClassName="overflow-hidden rounded-2xl border border-white/10"
                  className="h-auto w-full"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold">{title}</h3>
                <p className="mt-4 text-lg leading-relaxed text-gray-400">{text}</p>
              </div>
            </article>
          ))}
        </div>
      </ProductSection>

    </ProductPage>
  );
};

export default SolutionsPage;
